/**
 * useHasTranscriptionKey — pre-flight check for any audio transcription UI.
 *
 * Rules:
 *   - Admins always pass (they use platform keys server-side).
 *   - Non-admin users must have at least one BYOK key for `openai` or
 *     `google` (Gemini) in `valid` or `pending` status.
 *
 * Returns a small status object so callers can either block the UI or show
 * a contextual prompt to configure keys.
 */

import { useMemo } from "react";
import { useByokKeys } from "./useByokKeys";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import type { ByokKey, ProviderId } from "../types";
import { OPENAI_ONLY_MODE } from "@/lib/ai/providerPolicy";

const TRANSCRIPTION_PROVIDERS: ProviderId[] = OPENAI_ONLY_MODE ? ["openai"] : ["openai", "google"];

export interface TranscriptionKeyStatus {
  /** Final allow/block decision for transcription UI. */
  canTranscribe: boolean;
  /** True while either profile or BYOK list is still loading. */
  isLoading: boolean;
  /** True if the user has at least one usable transcription key. */
  hasUsableKey: boolean;
  /** Per-provider availability — handy for messaging. */
  providers: Record<"openai" | "google", boolean>;
  /** Admin shortcut — always allowed regardless of BYOK. */
  isAdmin: boolean;
  /** Reason for block, useful for UI copy. */
  reason: "loading" | "ok" | "missing_byok";
}

function isUsable(k: ByokKey): boolean {
  // `pending` keys haven't been health-probed yet but are still attemptable.
  // The backend will fail and surface the real error if the key is bad.
  return k.status === "valid" || k.status === "pending";
}

export function useHasTranscriptionKey(): TranscriptionKeyStatus {
  const { isAdmin, isLoading: userLoading } = useCurrentUser();
  const { data, isLoading: byokLoading } = useByokKeys();

  return useMemo<TranscriptionKeyStatus>(() => {
    const isLoading = userLoading || byokLoading;

    if (isAdmin) {
      return {
        canTranscribe: true,
        isLoading,
        hasUsableKey: true,
        providers: { openai: true, google: !OPENAI_ONLY_MODE },
        isAdmin: true,
        reason: "ok",
      };
    }

    if (isLoading) {
      return {
        canTranscribe: false,
        isLoading: true,
        hasUsableKey: false,
        providers: { openai: false, google: false },
        isAdmin: false,
        reason: "loading",
      };
    }

    const list = (data ?? []) as ByokKey[];
    const providers = {
      openai: list.some((k) => k.provider === "openai" && isUsable(k)),
      google: list.some((k) => k.provider === "google" && isUsable(k)),
    };
    const hasUsableKey = TRANSCRIPTION_PROVIDERS.some(
      (p) => p === "openai" ? providers.openai : providers.google,
    );

    return {
      canTranscribe: hasUsableKey,
      isLoading: false,
      hasUsableKey,
      providers,
      isAdmin: false,
      reason: hasUsableKey ? "ok" : "missing_byok",
    };
  }, [data, byokLoading, isAdmin, userLoading]);
}
